import moment from 'moment-timezone';
import { IMPACT_OWNER_FIRSTNAME, IMPACT_OWNER_LASTNAME } from '@/impactReports/impactReport.types';
import { getKPIItemColumnName } from '@/impactReports/impactReportDisplay.utilities';
import { validateGuid } from '@/utilities/guid.utilities';
import { SeeqNames } from '@seeqdev/constants';
import type { Moment } from 'moment';
import type { ImpactReportCategory, ImpactType, Location } from '@/impactReports/impactReport.types';
import type { ColumnDefinitionOutputV1 } from '@seeqdev/api/model/ColumnDefinitionOutputV1';

export type ImpactReportKpiValue =
  | { mode: 'manual'; amount: string | number }
  | { mode: 'calculation'; item?: { id: string } };

/** Changing controls also clears the value that the newly selected control cannot edit. */
export const changeImpactReportKpiMode = (
  value: ImpactReportKpiValue | undefined,
  mode: ImpactReportKpiValue['mode'],
): ImpactReportKpiValue => (value?.mode === mode ? value : mode === 'manual' ? { mode, amount: '' } : { mode });

export type ImpactReportDraft = {
  title: string;
  description: string;
  owner: { id: string; name: string };
  site: string;
  asset: string;
  impactType: ImpactType;
  impactStartDate?: Moment;
  impactEndDate?: Moment;
  impactFrequency: string;
  workbookId?: string;
  worksheetId?: string;
  location?: Location;
  kpiValues: Record<string, ImpactReportKpiValue | undefined>;
  expectedAnnualValues: Record<string, string | undefined>;
  categoryValues: Record<string, string | undefined>;
  reviewComment: string;
};

/** Hydrates the editable fields without mutating the queried row or replacing KPI item references with amounts. */
export const loadImpactReportDraft = (
  report: Record<string, string | null | undefined>,
  kpiColumns: ColumnDefinitionOutputV1[],
  expectedAnnualColumns: ColumnDefinitionOutputV1[],
  categories: ImpactReportCategory[] = [],
): ImpactReportDraft => {
  const startDate = report[SeeqNames.ImpactReport.ImpactStartDate];
  const endDate = report[SeeqNames.ImpactReport.ImpactEndDate];
  const location = report[SeeqNames.ImpactReport.Location];
  const impactType = report[SeeqNames.ImpactReport.ImpactSavingsType];
  return {
    title: report[SeeqNames.ImpactReport.ImpactName] ?? '',
    description: report[SeeqNames.ImpactReport.ImpactDescription] ?? '',
    owner: {
      id: report[SeeqNames.ImpactReport.ImpactOwner] ?? '',
      name: `${report[IMPACT_OWNER_FIRSTNAME] ?? ''} ${report[IMPACT_OWNER_LASTNAME] ?? ''}`.trim(),
    },
    site: report[SeeqNames.ImpactReport.ImpactSite] ?? '',
    asset: report[SeeqNames.ImpactReport.ImpactAsset] ?? '',
    impactType: impactType === 'continuous' || impactType === 'customCalc' ? impactType : 'oneTime',
    impactStartDate: startDate ? moment(startDate) : undefined,
    impactEndDate: endDate ? moment(endDate) : undefined,
    impactFrequency: report[SeeqNames.ImpactReport.ImpactFrequency] ?? '',
    workbookId: report[SeeqNames.ImpactReport.AssignedToWorkbook] ?? '',
    worksheetId: report[SeeqNames.ImpactReport.AssignedToWorksheet] ?? '',
    location: location ? JSON.parse(location) : undefined,
    kpiValues: Object.fromEntries(
      kpiColumns.map(({ columnName }) => {
        const value = (report[getKPIItemColumnName(columnName)] || report[columnName]) ?? undefined;
        return [
          columnName,
          value && validateGuid(value)
            ? { mode: 'calculation', item: { id: value } }
            : { mode: 'manual', amount: value ?? '' },
        ];
      }),
    ),
    expectedAnnualValues: Object.fromEntries(
      expectedAnnualColumns.map(({ columnName }) => {
        const value = report[columnName];
        return [columnName, value == null ? undefined : String(value)];
      }),
    ),
    categoryValues: Object.fromEntries(
      categories.map(({ category }) => [category.id!, report[category.id!] ?? undefined]),
    ),
    reviewComment: report[SeeqNames.ImpactReport.ReviewComment] ?? '',
  };
};

const nonEmptyValues = (values: Record<string, string | undefined>): Record<string, string> =>
  Object.fromEntries(
    Object.entries(values).filter((entry): entry is [string, string] => entry[1] !== undefined && entry[1] !== ''),
  );

/** The same normalized values drive change detection and persistence; blank dynamic fields are absent in both. */
export const serializeImpactReportDraft = (
  draft: ImpactReportDraft,
): {
  properties: Record<string, string>;
  categoryValues: Record<string, string>;
} => ({
  properties: nonEmptyValues({
    [SeeqNames.ImpactReport.ImpactName]: draft.title,
    [SeeqNames.ImpactReport.ImpactDescription]: draft.description,
    [SeeqNames.ImpactReport.ImpactOwner]: draft.owner.id,
    [SeeqNames.ImpactReport.ImpactSite]: draft.site,
    [SeeqNames.ImpactReport.ImpactAsset]: draft.asset,
    [SeeqNames.ImpactReport.ImpactSavingsType]: draft.impactType,
    [SeeqNames.ImpactReport.ImpactStartDate]: draft.impactStartDate?.isValid()
      ? draft.impactStartDate.toISOString()
      : undefined,
    [SeeqNames.ImpactReport.ImpactEndDate]: draft.impactEndDate?.isValid()
      ? draft.impactEndDate.toISOString()
      : undefined,
    [SeeqNames.ImpactReport.ImpactFrequency]: draft.impactFrequency,
    [SeeqNames.ImpactReport.AssignedToWorkbook]: draft.workbookId,
    [SeeqNames.ImpactReport.AssignedToWorksheet]: draft.worksheetId,
    [SeeqNames.ImpactReport.Location]: draft.location ? JSON.stringify(draft.location) : undefined,
    ...Object.fromEntries(
      Object.entries(draft.kpiValues).map(([column, value]) => [
        column,
        value?.mode === 'calculation' ? value.item?.id : value === undefined ? undefined : String(value.amount),
      ]),
    ),
    ...draft.expectedAnnualValues,
  }),
  categoryValues: nonEmptyValues(draft.categoryValues),
});
